/* import __COLOCATED_TEMPLATE__ from './actor-type-filter.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { REPORTING_FILTER_SELECT_ALL } from 'embercom/lib/reporting/flexible/constants';
import type IntlService from 'ember-intl/services/intl';
import type { SelectedFilter } from 'embercom/services/filter-pattern-service';

interface Args {
  selected: SelectedFilter;
  loadKnownValuesSources: string[];
  analyticsObject: string;
  setSelected: (filterValues: string[], operator?: string) => void;
  removeFilter: () => void;
  initiallyOpened: boolean;
  icon: string;
  type: string;
  name: string;
  isDisabled: boolean;
}

interface Item {
  text: string;
  value: string;
  isSelected: boolean;
}

export default class ActorTypeFilter extends Component<{ Args: Args }> {
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @service declare intl: IntlService;
  @tracked selectedDropdownItems = this.args.selected.values || [];
  @tracked operator = this.args.selected.operator || 'category';
  @tracked label = this.currentLabel;

  get actorTypeLabels() {
    return {
      ai_agent: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.actor-type-filter.ai_agent',
      ),
      teammate: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.actor-type-filter.teammate',
      ),
      workflow: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.actor-type-filter.workflow',
      ),
    } as const;
  }

  get allItems(): Item[] {
    return Object.entries(this.actorTypeLabels).map(([value, text]) => ({
      text,
      value,
      isSelected: this.selectedDropdownItems.includes(value),
    }));
  }

  get selectedActorTypes() {
    return this.selectedDropdownItems.filter((item) => item !== REPORTING_FILTER_SELECT_ALL);
  }

  get currentLabel() {
    if (this.selectedActorTypes.length === 0) {
      return this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.actor-type-filter.any',
      );
    }

    let types = this.selectedActorTypes.map((type) => {
      return this.actorTypeLabels[type as keyof typeof this.actorTypeLabels];
    });
    return `${types.join(', ')}`;
  }

  @action
  trackAnalytics() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'filtered_actor_type',
      object: this.args.analyticsObject,
      actor_types: this.selectedActorTypes,
      operator: this.operator,
    });
  }

  @action
  onClose() {
    if (this.isDestroying) {
      return;
    }

    this.label = this.currentLabel;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ChartBuilder::FilterBar::ActorTypeFilter': typeof ActorTypeFilter;
    'reporting/custom/chart-builder/filter-bar/actor-type-filter': typeof ActorTypeFilter;
  }
}
